import type { Loadable } from '../api/client';
import { Icon } from './Icon';

interface Props {
  state: Loadable<unknown>;
  title?: string;
  /** Shown when the request succeeded but the source held no rows. */
  emptyMessage?: string;
  onRetry?: () => void;
}

/**
 * Renders the non-success states of a request. The point of this component is
 * that "not connected", "no rows", and "the request failed" look different from
 * each other and none of them look like data.
 */
export function StateBlock({ state, title, emptyMessage, onRetry }: Props): JSX.Element | null {
  if (state.status === 'ready') {
    if (!emptyMessage) return null;
    return (
      <div className="state-block state-block--empty">
        <p className="state-block__message">{emptyMessage}</p>
      </div>
    );
  }

  if (state.status === 'idle') return null;

  if (state.status === 'loading') {
    return (
      <div className="state-block state-block--loading">
        <span className="state-block__spinner" />
        <p className="state-block__message">Loading</p>
      </div>
    );
  }

  if (state.status === 'not_configured') {
    return (
      <div className="state-block state-block--config">
        <div className="state-block__head">
          <Icon name="alert" size={16} />
          <span>{title ?? 'Source not connected'}</span>
        </div>
        <p className="state-block__message">{state.message}</p>
        {state.requires.length > 0 ? (
          <ul className="state-block__requires">
            {state.requires.map((key) => (
              <li key={key}>
                <code>{key}</code>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }

  return (
    <div className="state-block state-block--error">
      <div className="state-block__head">
        <Icon name="alert" size={16} />
        <span>{title ?? 'Request failed'}</span>
      </div>
      <p className="state-block__message">{state.message}</p>
      {onRetry ? (
        <button type="button" className="btn btn--ghost" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}
